<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard headline-id="dayOrder" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('dayOrder')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('dayOrder')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('dayOrder')"
                    @enter-edit-mode="onEnterEditMode('dayOrder', $t('day-order'))"
                    @leave-edit-mode="leaveEditMode('dayOrder')"
                    @discard-changes="discardChanges('dayOrder')"
                    @save="saveForm('dayOrder')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('day-order') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesDayOrderForm
                        v-if="showForm('dayOrder')"
                        ref="eventSeriesDayOrderFormRef"
                        class="p-5"
                        :series-id="series.id"
                    />
                    <SeriesDayOrderDisplay v-else :series-id="seriesId" />
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="appointmentRules" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('appointmentRules')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('appointmentRules')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('appointmentRules')"
                    @enter-edit-mode="onEnterEditMode('appointmentRules', $t('appointment-rules'))"
                    @leave-edit-mode="leaveEditMode('appointmentRules')"
                    @discard-changes="discardChanges('appointmentRules')"
                    @save="saveForm('appointmentRules')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('appointment-rules') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesAppointmentRulesForm
                        v-if="showForm('appointmentRules')"
                        ref="eventSeriesAppointmentRulesFormRef"
                        class="p-5"
                        :series-id="series.id"
                    />
                    <SeriesAppointmentRulesDisplay v-else :series-id="seriesId" />
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="cycle" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('cycle')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('cycle')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('cycle')"
                    @enter-edit-mode="onEnterEditMode('cycle', $t('cycle'))"
                    @leave-edit-mode="leaveEditMode('cycle')"
                    @discard-changes="discardChanges('cycle')"
                    @save="saveForm('cycle')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('cycle') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesCycleForm
                        v-if="showForm('cycle')"
                        ref="eventSeriesCycleFormRef"
                        class="p-5"
                        :series-id="series.id"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('cycle')"
                            :text="idToName(series.cycle, CYCLE_ITEMS)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('cycle-changes')"
                            :text="valueToString(series.cycleChanges)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import SeriesAppointmentRulesDisplay from '@/event/displays/series/SeriesAppointmentRulesDisplay.vue';
import SeriesDayOrderDisplay from '@/event/displays/series/SeriesDayOrderDisplay.vue';
import { idToName } from '@/event/event';
import { CYCLE_ITEMS } from '@/event/forms/schemas';
import EventSeriesAppointmentRulesForm from '@/event/forms/series/EventSeriesAppointmentRulesForm.vue';
import EventSeriesCycleForm from '@/event/forms/series/EventSeriesCycleForm.vue';
import EventSeriesDayOrderForm from '@/event/forms/series/EventSeriesDayOrderForm.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { $t } from '@/plugins/fluent';
import { useFluent } from 'fluent-vue';
import { computed, onUnmounted, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const props = defineProps<{ seriesId: string }>();

const fluent = useFluent();
const seriesId = computed(() => props.seriesId);
const { series } = useEventSeriesInjection();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    getCardStatus,
} = useActiveCard();

const eventSeriesDayOrderFormRef = useTemplateRef('eventSeriesDayOrderFormRef');
const eventSeriesAppointmentRulesFormRef = useTemplateRef('eventSeriesAppointmentRulesFormRef');
const eventSeriesCycleFormRef = useTemplateRef('eventSeriesCycleFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('dayOrder', eventSeriesDayOrderFormRef);
register('appointmentRules', eventSeriesAppointmentRulesFormRef);
register('cycle', eventSeriesCycleFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
onUnmounted(() => resetActiveCard());
</script>
